/**
 * @fileoverview Manual Meta ad spend import API: preview, commit, coverage and server error extraction.
 * @module features/ad-spends/api/adSpendImportService
 */

import { isAxiosError } from 'axios'
import apiClient from '@/lib/apiClient'
import {
  importCoverageSchema,
  importErrorBodySchema,
  metaImportPreviewSchema,
  metaImportResultSchema,
  type ImportCoverage,
  type MetaImportPreview,
  type MetaImportResult,
} from '../schemas/adSpendImportSchema'

const META_IMPORT_BASE = '/ad-spend-imports/meta'

/** Server validation message plus per-row / per-day details, ready to display. */
export interface ImportErrorInfo {
  message: string
  details: string[]
}

function toFormData(files: readonly File[]): FormData {
  const form = new FormData()
  files.forEach(file => form.append('files', file))
  return form
}

/** Validates both exports server-side and returns what would be imported. Writes nothing. */
export async function previewMetaImport(files: readonly File[]): Promise<MetaImportPreview> {
  const res = await apiClient.post(`${META_IMPORT_BASE}/preview`, toFormData(files))
  const parsed = metaImportPreviewSchema.safeParse(res.data)
  if (!parsed.success) throw new Error(`API validation failed for POST ${META_IMPORT_BASE}/preview`)
  return parsed.data
}

/** Replaces the org's Meta spend over the files' date range. */
export async function commitMetaImport(files: readonly File[]): Promise<MetaImportResult> {
  const res = await apiClient.post(`${META_IMPORT_BASE}/commit`, toFormData(files))
  const parsed = metaImportResultSchema.safeParse(res.data)
  if (!parsed.success) throw new Error(`API validation failed for POST ${META_IMPORT_BASE}/commit`)
  return parsed.data
}

/** Last day with stored Meta spend (API sync or import). */
export async function getMetaImportCoverage(): Promise<ImportCoverage> {
  const res = await apiClient.get(`${META_IMPORT_BASE}/coverage`)
  const parsed = importCoverageSchema.safeParse(res.data)
  if (!parsed.success) throw new Error(`API validation failed for GET ${META_IMPORT_BASE}/coverage`)
  return parsed.data
}

/** Extracts the server's validation message and details from a failed import request. */
export function toImportErrorInfo(error: unknown): ImportErrorInfo {
  if (isAxiosError(error)) {
    const body = importErrorBodySchema.safeParse(error.response?.data)
    if (body.success) return { message: body.data.message, details: body.data.details?.details ?? [] }
  }
  return { message: error instanceof Error ? error.message : 'Import failed', details: [] }
}
